import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { JobService } from 'src/@core/services/job/job.service';

const PROJECT_PAGE_SIZE = 10;

@Component({
  selector: 'hour4u-admin-entity-project-filter',
  templateUrl: './entity-project-filter.component.html',
  styleUrls: ['./entity-project-filter.component.scss']
})
export class EntityProjectFilterComponent implements OnInit, OnChanges {
  @Input() entityId: string;
  @Input() projects: any[] = [];
  @Input() selectedProjectId: string | null = null;
  @Input() disabled = false;
  @Input() showSearch = false;
  @Input() searchQuery = '';
  @Input() searchPlaceholder = 'Search lead by name or number...';
  @Input() wrapperClass = '';
  @Input() allowAllProjects = true;

  @Output() projectChange = new EventEmitter<string | null>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() projectsChange = new EventEmitter<any[]>();
  @Output() loadingChange = new EventEmitter<boolean>();

  displayedProjects: any[] = [];
  totalElements = 0;
  isLoading = false;
  isOpen = false;

  private currentPage = 1;
  private useExternalProjects = false;
  private projectsCache = new Map<string, any>();

  constructor(
    private jobService: JobService,
    private elementRef: ElementRef,
  ) { }

  ngOnInit() {
    this.initProjects();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.entityId && !changes.entityId.firstChange) {
      this.initProjects();
    }
    if (changes.projects && !changes.projects.firstChange && this.projects.length) {
      this.useExternalProjects = true;
      this.syncExternalProjects();
    }
    if (changes.selectedProjectId && this.selectedProjectId) {
      this.ensureSelectedProjectCached();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isOpen) {
      return;
    }
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
    }
  }

  get visibleProjects(): any[] {
    return this.useExternalProjects ? this.projects : this.displayedProjects;
  }

  get totalPages(): number {
    if (this.useExternalProjects) {
      return 1;
    }
    return Math.max(1, Math.ceil(this.totalElements / PROJECT_PAGE_SIZE));
  }

  get isTriggerDisabled(): boolean {
    return this.disabled || this.isLoading || (!this.useExternalProjects && !this.totalElements && !this.isOpen);
  }

  get pageNumber(): number {
    return this.currentPage;
  }

  get isExternalProjects(): boolean {
    return this.useExternalProjects;
  }

  get selectedLabel(): string {
    if (this.isLoading && !this.visibleProjects.length) {
      return 'Loading projects…';
    }
    if (this.selectedProjectId == null || this.selectedProjectId === '') {
      return this.allowAllProjects ? 'All projects' : 'Select a project';
    }
    const project = this.projectsCache.get(this.selectedProjectId)
      || this.visibleProjects.find((p) => p.project_id === this.selectedProjectId);
    if (project) {
      return project.title || project.project_name || 'Selected project';
    }
    return 'Selected project';
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    if (this.isTriggerDisabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen && !this.useExternalProjects && !this.displayedProjects.length && this.entityId) {
      this.fetchProjects(1);
    }
  }

  selectProject(projectId: string | null, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.isOpen = false;
    this.projectChange.emit(projectId);
  }

  goToPage(page: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    if (this.useExternalProjects || page < 1 || page > this.totalPages || page === this.currentPage || this.isLoading) {
      return;
    }
    this.fetchProjects(page);
  }

  getProjectLabel(project: any): string {
    return project.title || project.project_name || 'Untitled project';
  }

  isProjectSelected(projectId: string | null): boolean {
    if (projectId == null) {
      return this.selectedProjectId == null || this.selectedProjectId === '';
    }
    return this.selectedProjectId === projectId;
  }

  private initProjects() {
    if (this.entityId) {
      this.useExternalProjects = false;
      this.resetAndLoad();
      return;
    }
    if (this.projects.length) {
      this.useExternalProjects = true;
      this.syncExternalProjects();
    }
  }

  private syncExternalProjects() {
    this.displayedProjects = [...this.projects];
    this.cacheProjects(this.projects);
    this.projectsChange.emit(this.getCachedProjects());
  }

  private resetAndLoad() {
    this.currentPage = 1;
    this.totalElements = 0;
    this.displayedProjects = [];
    this.projectsCache.clear();
    this.projectsChange.emit([]);
    this.fetchProjects(1);
  }

  private fetchProjects(page: number) {
    if (!this.entityId) {
      return;
    }

    this.isLoading = true;
    this.loadingChange.emit(true);

    this.jobService.getAllProjects(this.entityId, page, PROJECT_PAGE_SIZE, 'created_at', 'desc', []).subscribe(
      (res: any) => {
        const items = (res && res.items) || [];
        const total = res && res.pagination && res.pagination.totalElements != null
          ? Number(res.pagination.totalElements)
          : items.length;

        this.currentPage = page;
        this.totalElements = total;
        this.displayedProjects = items;
        this.cacheProjects(items);
        this.projectsChange.emit(this.getCachedProjects());
        this.ensureSelectedProjectCached();

        this.isLoading = false;
        this.loadingChange.emit(false);
      },
      () => {
        if (page === 1) {
          this.displayedProjects = [];
          this.totalElements = 0;
          this.projectsCache.clear();
          this.projectsChange.emit([]);
        }
        this.isLoading = false;
        this.loadingChange.emit(false);
      }
    );
  }

  private cacheProjects(items: any[]) {
    items.forEach((project) => {
      if (project && project.project_id) {
        this.projectsCache.set(project.project_id, project);
      }
    });
  }

  private getCachedProjects(): any[] {
    return Array.from(this.projectsCache.values());
  }

  private ensureSelectedProjectCached() {
    if (!this.selectedProjectId || this.projectsCache.has(this.selectedProjectId) || !this.entityId) {
      return;
    }
    this.jobService.getHoaProject(this.entityId, this.selectedProjectId).subscribe(
      (project: any) => {
        if (project && project.project_id) {
          this.cacheProjects([project]);
          this.projectsChange.emit(this.getCachedProjects());
        }
      },
      () => { }
    );
  }
}
